import { useState, useEffect, useRef } from "react";
import { GraduationCap, Target, Eye, Users, ChevronRight } from "lucide-react";

const PILLAR_STYLES = {
  Academics: { badge: "bg-amber-500/20 text-amber-400", border: "border-amber-500/30", bg: "from-amber-500/10", dot: "bg-amber-400" },
  "Skill/IQ": { badge: "bg-red-500/20 text-red-400", border: "border-red-500/30", bg: "from-red-500/10", dot: "bg-red-400" },
  Exposure: { badge: "bg-blue-500/20 text-blue-400", border: "border-blue-500/30", bg: "from-blue-500/10", dot: "bg-blue-400" },
  Recruitment: { badge: "bg-green-500/20 text-green-400", border: "border-green-500/30", bg: "from-green-500/10", dot: "bg-green-400" },
};

// Each scenario has a different ordering/emphasis of milestones
const SCENARIOS = [
  {
    id: "3rd-grader",
    label: "3rd Grader",
    subtitle: "Early Foundation",
    grade: "3rd Grade",
    desc: "Starting at 8 years old. Maximum time in the system means maximum Tuition Rewards — up to 8,400+ points by signing day — plus years of verified development.",
    steps: [
      { mo: "1", title: "Family Enrollment", desc: "Parents set up the NSB Connect profile. Begin banking Tuition Rewards points immediately.", pillar: "Academics" },
      { mo: "2", title: "Points Start", desc: "100+ points/month from day one. By signing day, that's up to 8,400+ points.", pillar: "Academics" },
      { mo: "3", title: "Intro to Baseball", desc: "Age-appropriate skills assessment. Coaches identify natural strengths early.", pillar: "Skill/IQ" },
      { mo: "4", title: "Fun First", desc: "Development camps focused on fundamentals, love of the game, and athleticism.", pillar: "Skill/IQ" },
      { mo: "5", title: "Early Profile", desc: "Basic NSB profile created. A head start on building a scouting record.", pillar: "Exposure" },
      { mo: "6", title: "Academic Tracking", desc: "GPA tracking begins early. Builds habits for NCAA eligibility down the road.", pillar: "Academics" },
      { mo: "7", title: "Summer Camp", desc: "Age-group development camp. Skills, teamwork, and baseball IQ foundations.", pillar: "Skill/IQ" },
      { mo: "8", title: "First Video", desc: "Upload first game clips. Start building a video history coaches will see later.", pillar: "Exposure" },
      { mo: "9", title: "Points Growing", desc: "Already 900+ points banked in year one. A college scholarship is being built.", pillar: "Academics" },
      { mo: "10", title: "Progress Check", desc: "Coaches review athletic development. Set goals for the next year.", pillar: "Skill/IQ" },
      { mo: "11", title: "Family Plan", desc: "Points transferable to siblings. The whole family benefits from early enrollment.", pillar: "Academics" },
      { mo: "12", title: "Year 1 of 9", desc: "1,200 points banked. 8 more years to compound development, data, and savings.", pillar: "Recruitment" },
    ],
  },
  {
    id: "5th-grader",
    label: "5th Grader",
    subtitle: "Building Early",
    grade: "5th Grade",
    desc: "Starting at 10. Seven years until signing day means up to 7,200+ Tuition Rewards points, years of verified data, and a development arc no late-starter can replicate.",
    steps: [
      { mo: "1", title: "Profile Created", desc: "NSB athlete profile built with position, goals, and academic baseline.", pillar: "Exposure" },
      { mo: "2", title: "Tuition Rewards Start", desc: "Points banking immediately. 7 years = up to 7,200+ points by signing day.", pillar: "Academics" },
      { mo: "3", title: "Youth Combine", desc: "First age-appropriate combine. FlightScope baseline establishes starting metrics.", pillar: "Skill/IQ" },
      { mo: "4", title: "Fundamentals Camp", desc: "Throwing mechanics, batting stance, fielding technique. Build the right habits early.", pillar: "Skill/IQ" },
      { mo: "5", title: "Academic Habits", desc: "GPA tracking and study habits established. NCAA eligibility starts in the classroom.", pillar: "Academics" },
      { mo: "6", title: "Data Goes Live", desc: "First verified metrics on your profile. Early visibility in the NSB system.", pillar: "Exposure" },
      { mo: "7", title: "Summer Development", desc: "Multi-week camp focused on position skills and baseball IQ.", pillar: "Skill/IQ" },
      { mo: "8", title: "Progress Combine", desc: "Second data point. Measurable improvement tracked from baseline.", pillar: "Skill/IQ" },
      { mo: "9", title: "Video Library", desc: "Building a game footage library. Years of video when coaches start watching.", pillar: "Exposure" },
      { mo: "10", title: "Points Compounding", desc: "Already 1,200+ points in year one. By high school, thousands banked.", pillar: "Academics" },
      { mo: "11", title: "Year-End Review", desc: "Scout advisor reviews progress. Multi-year development plan mapped out.", pillar: "Recruitment" },
      { mo: "12", title: "Year 1 of 7", desc: "1,200 points. Verified baseline. 6 more years to build an unmatched profile.", pillar: "Recruitment" },
    ],
  },
  {
    id: "7th-grader",
    label: "7th Grader",
    subtitle: "Getting Serious",
    grade: "7th Grade",
    desc: "Starting at 12. Five years before signing day. Enough time for 6,000+ Tuition Rewards points and the verified data history that separates serious prospects from everyone else.",
    steps: [
      { mo: "1", title: "Athlete Profile", desc: "Full NSB profile with position, metrics goals, academic info, and highlight video.", pillar: "Exposure" },
      { mo: "2", title: "Baseline Combine", desc: "First real combine. FlightScope captures arm velocity, bat speed, sprint times.", pillar: "Skill/IQ" },
      { mo: "3", title: "Tuition Rewards", desc: "Points banking monthly. Five years = up to 6,000+ points by signing day.", pillar: "Academics" },
      { mo: "4", title: "Data Published", desc: "Verified metrics go live. You're in the NSB system before most kids even know it exists.", pillar: "Exposure" },
      { mo: "5", title: "Skills Camp", desc: "Position-specific development. Pitching, hitting, catching — based on combine results.", pillar: "Skill/IQ" },
      { mo: "6", title: "Academic Planning", desc: "GPA tracking active. NCAA core course requirements mapped for next 5 years.", pillar: "Academics" },
      { mo: "7", title: "Mid-Year Combine", desc: "Second combine. Track growth. Build the multi-year data arc that impresses coaches.", pillar: "Skill/IQ" },
      { mo: "8", title: "GameDay+ Debut", desc: "First games captured on GameDay+ broadcast. Video archive begins.", pillar: "Exposure" },
      { mo: "9", title: "Development Plan", desc: "Scout advisor sets a 5-year roadmap: skills to develop, events to attend, goals to hit.", pillar: "Recruitment" },
      { mo: "10", title: "Fall Combine", desc: "Third data point in year one. Three verified snapshots of your abilities.", pillar: "Skill/IQ" },
      { mo: "11", title: "Points Growing", desc: "1,200 points in year one. Four more years of compounding toward college savings.", pillar: "Academics" },
      { mo: "12", title: "Year 1 of 5", desc: "Verified data, points banking, development plan set. A 4-year head start on peers.", pillar: "Recruitment" },
    ],
  },
  {
    id: "8th-grader",
    label: "8th Grader",
    subtitle: "Just Getting Started",
    grade: "8th Grade",
    desc: "Building the foundation. Focus on academics, first exposure, and discovering their strengths.",
    steps: [
      { mo: "1", title: "Profile Build", desc: "Create your NSB athlete profile with academic info, position, and goals.", pillar: "Exposure" },
      { mo: "2", title: "Academic Foundation", desc: "Set up Tuition Rewards points. Start GPA tracking and NCAA eligibility planning.", pillar: "Academics" },
      { mo: "3", title: "First Combine", desc: "Attend an NSB Combine. Get your first FlightScope-verified baseline data.", pillar: "Skill/IQ" },
      { mo: "4", title: "Skills Assessment", desc: "Coaches review your baseline. Identify strengths and areas for development.", pillar: "Skill/IQ" },
      { mo: "5", title: "Development Plan", desc: "Position-specific skill camp assigned based on combine results.", pillar: "Skill/IQ" },
      { mo: "6", title: "Data Goes Live", desc: "Verified metrics published to your NSB profile. Early visibility begins.", pillar: "Exposure" },
      { mo: "7", title: "Tuition Banking", desc: "Points accumulating monthly. Review 400+ participating college list.", pillar: "Academics" },
      { mo: "8", title: "Summer Camp", desc: "Intensive development camp. Multiple sessions to accelerate growth.", pillar: "Skill/IQ" },
      { mo: "9", title: "Progress Combine", desc: "Second combine data point. Track measurable improvement.", pillar: "Skill/IQ" },
      { mo: "10", title: "Video Highlights", desc: "Upload first highlight reel. Coaches can begin reviewing your profile.", pillar: "Exposure" },
      { mo: "11", title: "Year-End Review", desc: "Scout advisor reviews progress. Set goals for high school recruitment.", pillar: "Recruitment" },
      { mo: "12", title: "Foundation Set", desc: "Verified data, academic tracking, and a roadmap for the next 3 years.", pillar: "Recruitment" },
    ],
  },
  {
    id: "10th-grader",
    label: "10th Grader",
    subtitle: "Building Momentum",
    grade: "10th Grade",
    desc: "Data is verified. Now it's about growth, exposure, and getting on college radars.",
    steps: [
      { mo: "1", title: "Profile Update", desc: "Refresh your NSB profile with updated stats, video, and academic transcript.", pillar: "Exposure" },
      { mo: "2", title: "Baseline Combine", desc: "First combine of the year. FlightScope captures current performance data.", pillar: "Skill/IQ" },
      { mo: "3", title: "Data Published", desc: "Verified metrics go live on the NSB Scout Wire. College coaches see you.", pillar: "Exposure" },
      { mo: "4", title: "Academic Audit", desc: "NCAA eligibility review. Tuition Rewards points banking toward college.", pillar: "Academics" },
      { mo: "5", title: "Position Camp", desc: "Intensive skill development. Pitching mechanics, hitting approach, or defense.", pillar: "Skill/IQ" },
      { mo: "6", title: "GameDay+ Exposure", desc: "NSB broadcasts your games. FlightScope data overlays reach college coaches.", pillar: "Exposure" },
      { mo: "7", title: "Mid-Year Combine", desc: "Second combine to track growth. Coaches see your improvement trajectory.", pillar: "Skill/IQ" },
      { mo: "8", title: "Target Schools", desc: "Scout advisor identifies 15-20 target programs based on your data + academics.", pillar: "Recruitment" },
      { mo: "9", title: "Coach Intro", desc: "NSB begins direct outreach to target coaching staffs with your verified data.", pillar: "Recruitment" },
      { mo: "10", title: "Fall Combine", desc: "Third data point of the year. Demonstrable growth over 8+ months.", pillar: "Skill/IQ" },
      { mo: "11", title: "Tuition Review", desc: "Thousands of points banked. Review participating schools with advisor.", pillar: "Academics" },
      { mo: "12", title: "Recruiting Ready", desc: "Strong verified profile, growing coach interest. Set up for junior year push.", pillar: "Recruitment" },
    ],
  },
  {
    id: "rising-junior",
    label: "Rising Junior",
    subtitle: "Peak Recruiting Window",
    grade: "11th Grade",
    desc: "This is the year. Maximum exposure, direct coach contact, and decision-making begins.",
    steps: [
      { mo: "1", title: "Recruiting Profile", desc: "Full profile overhaul. Updated video, verified data, academic transcript.", pillar: "Exposure" },
      { mo: "2", title: "Elite Combine", desc: "High-visibility combine. College coaches attend and evaluate in person.", pillar: "Skill/IQ" },
      { mo: "3", title: "Scout Wire Push", desc: "NSB pushes your data directly to 50+ target coaching staffs.", pillar: "Recruitment" },
      { mo: "4", title: "Coach Outreach", desc: "Direct introductions to D1/D2/D3/NAIA coaches matched to your profile.", pillar: "Recruitment" },
      { mo: "5", title: "Campus Visits", desc: "Scout advisor helps coordinate unofficial and official visit schedules.", pillar: "Recruitment" },
      { mo: "6", title: "GameDay+ Broadcast", desc: "Your games broadcast with real-time data overlays. Coaches watch remotely.", pillar: "Exposure" },
      { mo: "7", title: "Mid-Year Combine", desc: "Critical data point. Demonstrate elite-level performance to evaluators.", pillar: "Skill/IQ" },
      { mo: "8", title: "Academic Package", desc: "Transcripts, test scores, and Tuition Rewards summary sent to target schools.", pillar: "Academics" },
      { mo: "9", title: "Offer Evaluation", desc: "Scout advisor helps evaluate offers. Compare programs, aid packages, fit.", pillar: "Recruitment" },
      { mo: "10", title: "Decision Prep", desc: "Narrow to top 3-5 schools. Final visits, coach conversations, family meetings.", pillar: "Recruitment" },
      { mo: "11", title: "Tuition Leverage", desc: "Use accumulated points as guaranteed minimum scholarship at target schools.", pillar: "Academics" },
      { mo: "12", title: "Commitment", desc: "Verbal or signed commitment. Full verified profile delivered to your program.", pillar: "Recruitment" },
    ],
  },
  {
    id: "rising-senior",
    label: "Rising Senior",
    subtitle: "Closing the Deal",
    grade: "12th Grade",
    desc: "Final year. Securing commitments, signing day preparation, and maximizing scholarship value.",
    steps: [
      { mo: "1", title: "Final Profile", desc: "Complete, polished NSB profile with 2+ years of verified FlightScope data.", pillar: "Exposure" },
      { mo: "2", title: "Showcase Combine", desc: "Final combine performance. Maximum visibility to remaining target coaches.", pillar: "Skill/IQ" },
      { mo: "3", title: "Active Recruiting", desc: "NSB scouts in daily contact with coaching staffs advocating for you.", pillar: "Recruitment" },
      { mo: "4", title: "Signing Prep", desc: "NLI review, financial aid comparison, Tuition Rewards application submitted.", pillar: "Recruitment" },
      { mo: "5", title: "Academic Close", desc: "Final transcripts sent. Tuition Rewards points applied to aid package.", pillar: "Academics" },
      { mo: "6", title: "Early Signing", desc: "Early signing period. Lock in your commitment with your chosen program.", pillar: "Recruitment" },
      { mo: "7", title: "Spring Combine", desc: "Continue showcasing for any remaining opportunities or late offers.", pillar: "Skill/IQ" },
      { mo: "8", title: "GameDay+ Final", desc: "Senior season broadcast. Last chance for late recruiting attention.", pillar: "Exposure" },
      { mo: "9", title: "Late Signing", desc: "Late signing period for athletes still finalizing their decision.", pillar: "Recruitment" },
      { mo: "10", title: "Tuition Maximize", desc: "All accumulated points submitted. Guaranteed minimum scholarship secured.", pillar: "Academics" },
      { mo: "11", title: "Transition Prep", desc: "College preparation. Connect with incoming coaching staff and teammates.", pillar: "Recruitment" },
      { mo: "12", title: "College Ready", desc: "NSB-verified, college-committed, scholarship-secured. Your journey begins.", pillar: "Recruitment" },
    ],
  },
];

const AUTO_CYCLE_INTERVAL = 6000;

export function TimelineBoard() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  // Auto-cycle
  useEffect(() => {
    if (isPaused) return;
    timerRef.current = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveScenario((prev) => (prev + 1) % SCENARIOS.length);
        setTimeout(() => setIsAnimating(false), 100);
      }, 400);
    }, AUTO_CYCLE_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [isPaused]);

  const handleSelect = (idx) => {
    if (idx === activeScenario) return;
    setIsPaused(true);
    setIsAnimating(true);
    setTimeout(() => {
      setActiveScenario(idx);
      setTimeout(() => setIsAnimating(false), 100);
    }, 400);
    // Resume auto-cycle after 15s of inactivity
    setTimeout(() => setIsPaused(false), 15000);
  };

  const scenario = SCENARIOS[activeScenario];

  return (
    <div data-testid="timeline-board">
      {/* Scenario Selector */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6" data-testid="scenario-selector">
        {SCENARIOS.map((s, i) => (
          <button
            key={s.id}
            onClick={() => handleSelect(i)}
            className={`relative px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border text-left transition-all duration-300 ${
              i === activeScenario
                ? "border-red-500/50 bg-red-500/10 shadow-[0_0_20px_rgba(220,38,38,0.15)]"
                : "border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-white/20"
            }`}
            data-testid={`scenario-${s.id}`}
          >
            <span className={`text-xs font-black uppercase tracking-wider block ${i === activeScenario ? "text-red-400" : "text-white/40"}`}>{s.grade}</span>
            <span className={`text-[11px] block mt-0.5 ${i === activeScenario ? "text-white/70" : "text-white/30"}`}>{s.subtitle}</span>
            {i === activeScenario && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 rounded-full bg-red-500" />
            )}
          </button>
        ))}
      </div>

      {/* Scenario Description */}
      <div className={`text-center mb-6 transition-opacity duration-400 ${isAnimating ? "opacity-0" : "opacity-100"}`}>
        <p className="text-sm text-white/60 max-w-xl mx-auto italic">{scenario.desc}</p>
      </div>

      {/* Auto-cycle indicator */}
      <div className="flex justify-center gap-1.5 mb-6">
        {SCENARIOS.map((_, i) => (
          <div key={i} className="relative h-1 w-12 sm:w-16 rounded-full bg-white/10 overflow-hidden">
            {i === activeScenario && !isPaused && (
              <div className="absolute inset-y-0 left-0 bg-red-500 rounded-full animate-progress" />
            )}
            {i === activeScenario && isPaused && (
              <div className="absolute inset-0 bg-red-500/60 rounded-full" />
            )}
            {i < activeScenario && (
              <div className="absolute inset-0 bg-white/20 rounded-full" />
            )}
          </div>
        ))}
      </div>

      {/* Timeline Grid — animated */}
      <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 transition-all duration-500 ${isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"}`} data-testid="timeline-grid">
        {scenario.steps.map((step, i) => {
          const ps = PILLAR_STYLES[step.pillar];
          return (
            <div
              key={`${scenario.id}-${step.mo}`}
              className={`rounded-xl border ${ps.border} bg-gradient-to-b ${ps.bg} to-transparent p-4 transition-all duration-500`}
              style={{ animationDelay: `${i * 50}ms` }}
              data-testid={`timeline-step-${step.mo}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-xs font-black text-white">{step.mo}</span>
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${ps.badge}`}>{step.pillar}</span>
              </div>
              <h4 className="text-sm font-bold text-white mb-1">{step.title}</h4>
              <p className="text-[11px] text-white/50 leading-relaxed">{step.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Pillar Legend */}
      <div className="flex flex-wrap justify-center gap-4 mt-8">
        {Object.entries(PILLAR_STYLES).map(([label, style]) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
            <span className="text-xs text-white/50 font-medium">{label}</span>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress ${AUTO_CYCLE_INTERVAL}ms linear;
        }
      `}</style>
    </div>
  );
}
